import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import { stockQuantityAllowsFraction } from './inventoryUtils';
import type { InventoryFlowResult, TransferStockForm } from './inventoryOperations.types';

export function parseTransferStockForm(
  ctx: { canEdit: boolean; canTransfer?: boolean; branchId: string | null },
  form: TransferStockForm
): InventoryFlowResult<{
  productId: string;
  fromBranchId: string;
  toBranchId: string;
  quantity: number;
  waitForReceive: boolean;
  variantId: string | null;
}> {
  if (!ctx.canEdit) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'info',
        title: 'Solo lectura',
        message: 'Podés ver el stock, no transferirlo.',
      },
    };
  }
  if (!ctx.canTransfer) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'info',
        title: 'Sin permiso',
        message: 'No tenés permiso para transferir stock.',
      },
    };
  }
  if (form.destinationBranchCount === 0) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'error',
        title: 'Sin sucursal destino',
        message: 'Para transferir necesitás al menos dos sucursales.',
      },
    };
  }
  if (!form.selectedStock || !ctx.branchId || !form.destinationBranchId) {
    return { kind: 'blocked', notice: { tone: 'error', title: 'Error', message: 'Completá cantidad y sucursal destino.' } };
  }
  const allowsFraction = stockQuantityAllowsFraction(form.selectedStock.unitOfMeasure);
  const quantityNumber = parseDecimalInput(form.transferQuantity, {
    integerOnly: !allowsFraction,
    min: allowsFraction ? 0.0001 : 1,
  });
  if (quantityNumber === null || quantityNumber <= 0) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'error',
        title: 'Cantidad inválida',
        message: allowsFraction
          ? 'Ingresá una cantidad mayor a cero.'
          : 'Ingresá un entero mayor a cero.',
      },
    };
  }
  // Validación de variante: si viene variantId, debe ser un string no vacío.
  // La pertenencia variante↔producto (contra la cache RAM / SQLite) la valida el
  // operation layer vía assertVariantBelongsToProduct, que es donde hay acceso a
  // product_variants. Este comando es pure-sync y no puede resolver la variante.
  const variantId = (form.variantId ?? '').trim();
  if (form.variantId && !variantId) {
    return {
      kind: 'blocked',
      notice: { tone: 'error', title: 'Variante inválida', message: 'La variante seleccionada no es válida.' },
    };
  }
  return {
    kind: 'done',
    refresh: {
      stocks: true,
      branchActivity: true,
      dashboard: true,
      pendingTransfers: form.transferWaitForReceive,
    },
    data: {
      productId: form.selectedStock.productId,
      fromBranchId: ctx.branchId,
      toBranchId: form.destinationBranchId,
      quantity: quantityNumber,
      waitForReceive: form.transferWaitForReceive,
      variantId: variantId || null,
    },
  };
}

export function canOpenTransferEntry(ctx: { canEdit: boolean; canTransfer?: boolean; destinationBranchCount: number }): InventoryFlowResult {
  if (!ctx.canEdit) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'info',
        title: 'Solo lectura',
        message: 'Podés ver el stock, no transferirlo.',
      },
    };
  }
  if (!ctx.canTransfer) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'info',
        title: 'Sin permiso',
        message: 'No tenés permiso para transferir stock.',
      },
    };
  }
  if (ctx.destinationBranchCount === 0) {
    return {
      kind: 'blocked',
      notice: {
        tone: 'error',
        title: 'Sin sucursal destino',
        message: 'Para transferir necesitás al menos dos sucursales.',
      },
    };
  }
  return { kind: 'done', refresh: {} };
}
